import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Info, Plane, Truck, ChevronDown, Loader2, AlertCircle } from 'lucide-react';
import { useOrders } from '../hooks/useOrders';
import { useShippingMode } from '../context/ShippingModeContext';
import miscService from '../services/misc.service';

const RateCalculatorPage = () => {
  const { calculateAllRates, loading, error } = useOrders();
  const { shippingMode, isInternational, isDomestic } = useShippingMode();
  const [rateData, setRateData] = useState(null);
  const [calculating, setCalculating] = useState(false);

  // Form state
  const [pickupPincode, setPickupPincode] = useState('110044');
  const [deliveryPincode, setDeliveryPincode] = useState('110044');
  const [pickupCountry, setPickupCountry] = useState('India');
  const [deliveryCountry, setDeliveryCountry] = useState('India');
  const [packageType, setPackageType] = useState('Box');
  const [weight, setWeight] = useState(0.5); // in kg
  const [weightUnit, setWeightUnit] = useState('kg'); // 'kg' | 'g' — weight state always stays kg; converted only at the input
  const [dimensions, setDimensions] = useState({ length: 10, width: 10, height: 10 });
  const [paymentMode, setPaymentMode] = useState('prepaid');
  const [selectedCourierId, setSelectedCourierId] = useState(null);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      const list = await miscService.getCountries();
      setCountries(list);
    };
    fetchCountries();
  }, []);



  // Update countries when mode changes
  useEffect(() => {
    if (isInternational) {
      setDeliveryPincode('');
      setDeliveryCountry('');
    } else {
      setPickupCountry('India');
      setDeliveryCountry('India');
      setDeliveryPincode('110044');
    }
  }, [shippingMode, isInternational]);

  // Calculate rate when inputs change
  useEffect(() => {
    const hasPincode = isInternational
      ? (pickupPincode.trim() && deliveryPincode.trim())
      : (pickupPincode.trim().length >= 6 && deliveryPincode.trim().length >= 6);

    if (
      hasPincode &&
      weight > 0
    ) {
      const timer = setTimeout(async () => {
        setCalculating(true);
        try {
          const rateResponse = await calculateAllRates({
            pickupPincode,
            deliveryPincode,
            pickupCountry,
            deliveryCountry,
            weight,
            packageType,
            length: dimensions.length,
            width: dimensions.width,
            height: dimensions.height,
            orderType: shippingMode
          });

          if (rateResponse?.courierOptions?.length) {
            rateResponse.courierOptions = rateResponse.courierOptions.filter(opt => String(opt.id || opt.courier_id) !== '181' && !(opt.name || '').toLowerCase().includes('widect'));
          }
          setRateData(rateResponse);
          if (rateResponse?.courierOptions?.length) {
            setSelectedCourierId(String(rateResponse.courierOptions[0].id));
          }
        } catch (err) {
          console.error('Rate calculation error:', err);
          setRateData(null);
        } finally {
          setCalculating(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [pickupPincode, deliveryPincode, pickupCountry, deliveryCountry, weight, dimensions, shippingMode, paymentMode]);

  const selectedCourier = useMemo(() => {
    if (!rateData?.courierOptions?.length || !selectedCourierId) return null;
    return rateData.courierOptions.find((c) => String(c.id) === String(selectedCourierId));
  }, [rateData, selectedCourierId]);

  const displayTotal = selectedCourier
    ? selectedCourier.totalCharges
    : rateData?.totalAmount ?? rateData?.baseRate;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen font-sans text-slate-800">
      <h1 className="mb-6 text-xl font-bold text-[#1e293b]">Rate Calculator</h1>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        {/* Left Side: Input Form */}
        <div className="rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <div className="border-b-2 border-blue-600 bg-sky-100 px-6 py-4 text-center text-blue-600 font-bold">
            {isInternational ? 'International Shipping' : 'Domestic Shipping'}
          </div>

          <div className="px-5 py-6 space-y-6">
            {/* Pincode Section */}
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                {isInternational ? 'Pickup and delivery country / pincode' : 'Pickup and delivery pincode'}
              </label>

              {isInternational && (
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={pickupCountry}
                      list="pickup-country-list-calc"
                      onChange={(e) => setPickupCountry(e.target.value)}
                      placeholder="Pickup Country"
                      className="w-full rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold outline-none border-2 border-transparent focus:border-blue-500"
                    />
                    <datalist id="pickup-country-list-calc">
                      {countries.map((c, i) => <option key={i} value={c.name} />)}
                    </datalist>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={deliveryCountry}
                      list="delivery-country-list-calc"
                      onChange={(e) => setDeliveryCountry(e.target.value)}
                      placeholder="Delivery Country"
                      className="w-full rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold outline-none border-2 border-transparent focus:border-blue-500"
                    />
                    <datalist id="delivery-country-list-calc">
                      {countries.map((c, i) => <option key={i} value={c.name} />)}
                    </datalist>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={pickupPincode}
                    onChange={(e) => setPickupPincode(e.target.value)}
                    placeholder="Pickup pincode"
                    className="w-full rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold outline-none border-2 border-transparent focus:border-blue-500"
                  />
                </div>

                <div className="flex-none px-2 text-slate-300">-----</div>

                <div className="flex-1">
                  <input
                    type="text"
                    value={deliveryPincode}
                    onChange={(e) => setDeliveryPincode(e.target.value)}
                    placeholder="Delivery pincode"
                    className="w-full rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold outline-none border-2 border-transparent focus:border-blue-500"
                  />
                </div>
              </div>
            </div>


            {isDomestic && (
              <p className="text-xs text-slate-600 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 mt-2">
                Domestic shipping rates from trusted logistics partners. Create an order to book — 
                then use the order details to request pickup and download labels.
              </p>
            )}

            {/* Package Type and Weight */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">Package Type</label>
                <div className="relative">
                  <select
                    value={packageType}
                    onChange={(e) => setPackageType(e.target.value)}
                    className="w-full appearance-none rounded-lg bg-slate-100 px-4 py-3 text-sm outline-none"
                  >
                    <option>Plastic cover/Flyer</option>
                    <option>Box</option>
                    <option>Envelope</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-3.5 text-slate-400" size={16} />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold text-slate-700">Weight</label>
                <div className="flex rounded-lg bg-slate-100 overflow-hidden">
                  <input
                    type="number"
                    value={weightUnit === 'g' ? Math.round(weight * 1000) : weight}
                    onChange={(e) => setWeight(weightUnit === 'g' ? (parseFloat(e.target.value) || 0) / 1000 : parseFloat(e.target.value) || 0)}
                    min={weightUnit === 'g' ? 100 : 0.1}
                    step={weightUnit === 'g' ? 1 : 0.1}
                    className="w-full bg-transparent px-4 py-3 text-sm outline-none"
                  />
                  <select
                    value={weightUnit}
                    onChange={(e) => setWeightUnit(e.target.value)}
                    className="bg-slate-200 px-2 py-3 text-sm font-bold text-slate-500 outline-none cursor-pointer"
                  >
                    <option value="kg">kg</option>
                    <option value="g">g</option>
                  </select>
                </div>
                <p className="mt-2 text-[10px] leading-tight text-slate-400">
                  Package weight : sum of item's weight and weight of packaging (e.g. box)
                </p>
              </div>
            </div>

            {/* Volumetric Dimensions */}
            <div>
              <label className="mb-2 block text-xs font-bold text-slate-700">Dimensions (cm)</label>
              <div className="flex gap-2">
                {['length', 'width', 'height'].map((dim, idx) => (
                  <div key={idx} className="flex rounded-lg border border-slate-200 overflow-hidden">
                    <input
                      type="number"
                      value={dimensions[dim]}
                      onChange={(e) => setDimensions({ ...dimensions, [dim]: parseFloat(e.target.value) || 0 })}
                      min="0"
                      className="w-16 text-center text-sm outline-none px-2 py-2"
                      placeholder={dim.charAt(0).toUpperCase()}
                    />
                    <span className="bg-slate-50 px-2 py-2 text-[10px] font-bold text-slate-500 border-l border-slate-200">CM</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Info Box */}
            <div className="rounded-lg bg-slate-50 p-4 text-[11px] text-slate-600 space-y-2">
              <div className="flex items-center gap-2 font-bold text-slate-800">
                <Info size={14} className="text-slate-500" />
                Volumetric weight calculator
              </div>
              <div className="flex items-start gap-2 pl-5">
                <Plane size={12} className="mt-0.5" />
                <span>Express: L*B*H / Volumetric Divisor ({dimensions.length}×{dimensions.width}×{dimensions.height}/5000={(dimensions.length * dimensions.width * dimensions.height / 5000).toFixed(2)} kg)</span>
              </div>
              <div className="flex items-start gap-2 pl-5">
                <Truck size={12} className="mt-0.5" />
                <span>Surface: L*B*H / Volumetric Divisor ({dimensions.length}×{dimensions.width}×{dimensions.height}/5000={(dimensions.length * dimensions.width * dimensions.height / 5000).toFixed(2)} kg)</span>
              </div>
            </div>

            {/* Payment Mode — Domestic only */}
            {isDomestic && (
            <div>
              <label className="mb-3 block text-xs font-bold text-slate-700">Payment Mode</label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentMode === 'prepaid'}
                    onChange={() => setPaymentMode('prepaid')}
                    className="h-4 w-4 accent-blue-600"
                  />
                  Prepaid
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="payment"
                    checked={paymentMode === 'cod'}
                    onChange={() => setPaymentMode('cod')}
                    className="h-4 w-4 accent-blue-600"
                  />
                  Cash on Delivery (COD)
                </label>
              </div>
            </div>
            )}
          </div>
        </div>

        {/* Right Side: Results */}
        <div className="rounded-xl border border-slate-100 bg-white shadow-sm flex flex-col overflow-hidden">
          {/* Freight Charges */}
          <div className="border-b-2 border-blue-600 bg-sky-100 px-6 py-4 text-center text-blue-600 font-bold">
            Freight Charges
          </div>

          <div className="px-5 py-6 space-y-4 flex-grow">
            {calculating || loading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : rateData ? (
              <div className="space-y-4">
                {rateData.courierOptions?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[11px] font-bold text-slate-700 uppercase tracking-wide">
                        Available Courier options
                      </p>
                      <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                        {rateData.courierOptions.map((opt) => (
                          <label
                            key={String(opt.id)}
                            className={`flex flex-col gap-2 p-3 rounded-lg border cursor-pointer text-xs ${
                              String(selectedCourierId) === String(opt.id)
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200 bg-white'
                            }`}
                          >
                            <div className="flex items-start gap-2 w-full">
                              <input
                                type="radio"
                                name="rate-courier"
                                className="mt-0.5"
                                checked={String(selectedCourierId) === String(opt.id)}
                                onChange={() => setSelectedCourierId(String(opt.id))}
                              />
                              <span className="flex-1">
                                <span className="font-semibold text-slate-900 block">{opt.name}</span>
                                <span className="text-slate-500">
                                  Base {formatCurrency(opt.freightCharges)}
                                  {(opt.codCharges || 0) > 0 && (
                                    <> · COD {formatCurrency(opt.codCharges)}</>
                                  )}
                                  {opt.estimatedDelivery && (
                                    <> · {opt.estimatedDelivery}</>
                                  )}
                                </span>
                              </span>
                              <span className="font-bold text-blue-600 whitespace-nowrap">
                                {formatCurrency(opt.totalCharges)}
                              </span>
                            </div>
                            
                            {/* Render Detailed Charges Breakdown if available */}
                            {opt.detailedCharges && opt.detailedCharges.length > 0 && String(selectedCourierId) === String(opt.id) && (
                              <div className="mt-2 pl-6 space-y-1 text-[10px] text-slate-600 border-t border-slate-100 pt-2">
                                {opt.detailedCharges.filter(ch => ch.charge_name && (ch.charge_amount || ch.inr_amount || 0) > 0).map((charge, idx) => (
                                  <div key={idx} className="flex justify-between items-center">
                                    <span>{charge.charge_name}</span>
                                    <span className="font-medium text-slate-900">
                                      {formatCurrency(charge.charge_amount || charge.inr_amount || 0)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                <div className="rounded-xl bg-slate-50 p-5 relative">
                  <div className="mb-1 text-[11px] font-bold text-slate-800">
                    {selectedCourier?.name || rateData.courierName || rateData.serviceType || 'Express'}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-black">
                      {formatCurrency(
                        displayTotal ??
                          rateData.totalAmount ??
                          (rateData.baseRate || 0) + (rateData.additionalCharges || 0)
                      )}
                    </span>
                    <span className="text-xs font-medium text-slate-600">
                      / {rateData.estimatedDelivery || 'Estimated delivery'}
                    </span>
                  </div>

                  <div className="absolute top-4 right-5 text-slate-800 opacity-80">
                    <Plane size={32} strokeWidth={1.5} />
                  </div>

                  <div className="mt-4 flex items-start gap-2 border-t border-slate-200 pt-3 text-[10px] leading-relaxed text-slate-600">
                    <Info size={14} className="mt-0.5 flex-none text-slate-400" />
                    <div>
                      <p>
                        Shipping cost:{' '}
                        {formatCurrency(
                          selectedCourier?.freightCharges ?? rateData.baseRate ?? 30.0
                        )}
                      </p>
                      {(selectedCourier?.codCharges || rateData.additionalCharges) > 0 && (
                        <p>
                          + COD / other:{' '}
                          {formatCurrency(
                            selectedCourier?.codCharges ?? rateData.additionalCharges ?? 0
                          )}
                        </p>
                      )}
                      {rateData.gst ? (
                        <p>+ GST charge: {formatCurrency(rateData.gst)}</p>
                      ) : null}
                      {rateData.dph ? (
                        <p>+ DPH charge: {formatCurrency(rateData.dph)}</p>
                      ) : null}
                    </div>
                  </div>
                </div>

                <p className="text-[11px] text-slate-600 text-center">
                  Book shipment on{' '}
                  <Link to="/create-order" className="font-semibold text-blue-600 hover:underline">
                    Create order
                  </Link>
                  , then manage shipment from orders list.
                </p>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 text-sm">
                Enter pincode and weight to calculate rate
              </div>
            )}
          </div>

          {/* View Detailed Rate Card button — commented out, restore when needed */}
          {/* <div className="p-6 pt-0">
            <button className="w-full rounded-lg bg-[#111827] py-4 font-bold text-white transition hover:bg-slate-800">
              View Detailed Rate Card
            </button>
          </div> */}
        </div>

      </div>
    </div>
  );
};

export default RateCalculatorPage;
